import { Component } from '@theme/component';

const DISMISSED_KEY = 'ascent-whatsapp-label-dismissed';

/**
 * Floating WhatsApp link. The label renders visually hidden so the icon alone works without
 * JavaScript; this shows it until the shopper closes it or leaves a page where it was on screen,
 * then keeps it hidden for the session. On the homepage the button can wait until the first
 * section has scrolled out of the way.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} link
 * @property {HTMLElement} label
 * @property {HTMLButtonElement} [dismissButton]
 *
 * @extends Component<Refs>
 */
class WhatsappButton extends Component {
  requiredRefs = ['link', 'label'];

  /** @type {IntersectionObserver | undefined} */
  #firstSectionObserver;

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('pageshow', this.#syncLabel);
    window.addEventListener('pagehide', this.#closeLabelIfSeen);
    document.addEventListener('prerenderingchange', this.#syncLabel);

    this.#syncLabel();
    if (this.hasAttribute('data-after-first-section')) this.#revealAfterFirstSection();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('pageshow', this.#syncLabel);
    window.removeEventListener('pagehide', this.#closeLabelIfSeen);
    document.removeEventListener('prerenderingchange', this.#syncLabel);
    this.#firstSectionObserver?.disconnect();
  }

  dismiss() {
    this.#toggleLabel(false);
    this.refs.link.focus();
    closeForSession();
  }

  trackClick() {
    window.gtag?.('event', 'whatsapp_click');
    Shopify.analytics?.publish('whatsapp_click', {});
  }

  // A page restored by Back/Forward, or prerendered before the shopper arrived, still shows the label
  // as it was when the page first loaded, so the session's choice is read again each time it's shown.
  #syncLabel = () => {
    if (this.refs.dismissButton) this.#toggleLabel(!isDismissed());
  };

  // While the button itself is hidden (homepage hero, cookie banner) the label hasn't been seen yet,
  // so it greets again on the next page.
  #closeLabelIfSeen = () => {
    if (this.refs.label.classList.contains('visually-hidden')) return;
    if (getComputedStyle(this).visibility === 'hidden') return;
    closeForSession();
  };

  /** @param {boolean} visible */
  #toggleLabel(visible) {
    this.refs.label.classList.toggle('visually-hidden', !visible);
    if (this.refs.dismissButton) this.refs.dismissButton.hidden = !visible;
  }

  #revealAfterFirstSection() {
    const reveal = () => {
      this.#firstSectionObserver?.disconnect();
      this.removeAttribute('data-after-first-section');
    };
    const firstSection = document.querySelector('main .shopify-section');
    if (!firstSection) {
      reveal();
      return;
    }

    // The root is the lower half of the viewport, where the button sits.
    this.#firstSectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry && !entry.isIntersecting) reveal();
      },
      { rootMargin: '-50% 0px 0px 0px' }
    );
    this.#firstSectionObserver.observe(firstSection);
  }
}

function closeForSession() {
  if (Shopify.designMode) return;
  try {
    sessionStorage.setItem(DISMISSED_KEY, 'true');
  } catch {
    // Storage blocked: the label returns on the next page.
  }
}

function isDismissed() {
  try {
    return sessionStorage.getItem(DISMISSED_KEY) === 'true';
  } catch {
    return false;
  }
}

if (!customElements.get('whatsapp-button')) {
  customElements.define('whatsapp-button', WhatsappButton);
}
